import {Component, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {ActivatedRoute, Router, RouterLink} from "@angular/router"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {VerifyEmailGQL} from "@pages/verify-email/verify-email-page.generated"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {AuthService} from "@common/services/auth/auth.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-verify-email-page",
    templateUrl: "verify-email-page.component.html",
    styleUrls: ["verify-email-page.component.scss"],
    imports: [MatProgressSpinnerModule, RouterLink],
})
export class VerifyEmailPageComponent implements OnInit {
    readonly $isVerifying = signal(true)
    readonly $verificationSuccess = signal(false)
    readonly $errorMessage = signal<string | null>(null)

    readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)
    readonly verifyEmailGql = inject(VerifyEmailGQL)
    readonly notifications = inject(NotificationsService)
    readonly authService = inject(AuthService)

    async ngOnInit(): Promise<void> {
        const token = this.route.snapshot.queryParamMap.get("token")

        if (!token) {
            this.$errorMessage.set("No verification token provided")
            this.$isVerifying.set(false)
            return
        }

        await this.verifyEmail(token)
    }

    async verifyEmail(token: string): Promise<void> {
        try {
            const result = await this.verifyEmailGql
                .mutate({
                    token,
                })
                .toPromise()

            if (result?.data?.verifyEmail) {
                const response = result.data.verifyEmail

                // Complete login to update auth state
                if (response.token && response.user) {
                    await this.authService.completeLogin({
                        user: {id: response.user.uuid},
                        token: response.token,
                    })
                }

                this.$verificationSuccess.set(true)

                // Redirect to onboarding or pictures
                setTimeout(() => {
                    if (response.onboardingCompleted) {
                        this.router.navigate(["/pictures"])
                    } else {
                        this.router.navigate(["/onboarding"])
                    }
                }, 1000)
            }
        } catch (error) {
            const errorMessage = (error as Error).message || "Verification failed. The link may be invalid or expired."
            this.$errorMessage.set(errorMessage)
            this.notifications.showError(error)
        } finally {
            this.$isVerifying.set(false)
        }
    }
}
